/**
 * PlotCanvas — the SCROLL_CANVAS plot spine: every beat of every act in one
 * continuous scroll inside the workspace plot tab, on SectionCanvas.
 *
 * - Same stored objects as the level wizard ({ level_1..3 } maps keyed by
 *   beat key) — no schema change; saves go through the SAME autosave stack
 *   as PR 1 (useFormAutosave + createFormSave, content_rev threaded).
 * - AI hierarchy (review #7): the beat textarea is the primary surface;
 *   Forge Guide is a small secondary toggle per beat, closed by default.
 * - Scroll-spy reports the act in view (debounced) so the Plot Mentor's
 *   forge-guide scoping follows the writer.
 * - Focus mode isolates one beat; preference persists per project/device
 *   (localStorage — a view preference, not content, so no content_rev churn).
 * - Fetches its own project detail (never trusts the workspace LIST payload
 *   for level data or rev — see the projection pitfall in saveClient.js).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, ChevronDown, ChevronUp, Clapperboard, Crosshair, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { createFormSave } from '../lib/formAutosave';
import { useFormAutosave } from '../hooks/useFormAutosave';
import SaveStatusLabel from './SaveStatusLabel';
import SectionCanvas from './SectionCanvas';
import { AutoExpandTextarea } from './AutoExpandTextarea';
import { Button } from './ui/button';
import { LEVEL_CONFIG, beatsFor, forgeGuideText, normalizeFormat } from '../lib/plotBeats';
import { devLevel, groupDevLevel } from '../lib/devLevel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const focusStorageKey = (projectId) => `pangaji_canvas_focus_${projectId}`;

export default function PlotCanvas({ projectId, onActiveActChange }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({ 1: {}, 2: {}, 3: {} });
  const [stressWarning, setStressWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [openGuides, setOpenGuides] = useState(() => new Set());
  const [focusId, setFocusId] = useState(() => {
    try {
      return localStorage.getItem(focusStorageKey(projectId)) || null;
    } catch {
      return null;
    }
  });
  const saveClientRef = useRef(null);
  // Per-level last-saved JSON — persist only the levels that changed, so a
  // one-beat edit is one PUT, not three.
  const lastSavedRef = useRef({ 1: null, 2: null, 3: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`${API}/projects/${projectId}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`load failed (${response.status})`);
        const data = await response.json();
        if (cancelled) return;
        setProject(data);
        const levels = {
          1: data.level_1 || {},
          2: data.level_2 || {},
          3: data.level_3 || {},
        };
        setFormData(levels);
        lastSavedRef.current = {
          1: JSON.stringify(levels[1]),
          2: JSON.stringify(levels[2]),
          3: JSON.stringify(levels[3]),
        };
        // Re-key on project change; never regress a live client's rev.
        if (saveClientRef.current?.projectId !== projectId) {
          saveClientRef.current = {
            projectId,
            client: createFormSave({
              url: `${API}/projects/${projectId}`,
              initialRev: data.content_rev ?? null,
            }),
          };
        } else if (saveClientRef.current.client.getRev() == null) {
          saveClientRef.current.client.setRev(data.content_rev ?? null);
        }
      } catch (error) {
        console.error('Error loading plot canvas:', error);
        if (!cancelled) {
          setProject(null);
          toast.error('Failed to load project');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Saves only the levels whose content moved since their last landed save.
  const persistLevels = useCallback(
    async (data, meta) => {
      for (const lvl of [1, 2, 3]) {
        const json = JSON.stringify(data[lvl] || {});
        if (json === lastSavedRef.current[lvl]) continue;
        await saveClientRef.current.client.save(
          { level: lvl, data: data[lvl] || {}, completed: false },
          meta,
        );
        lastSavedRef.current[lvl] = json;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId],
  );
  // At page close only the FIRST dirty level's PUT can ride keepalive —
  // veto the no-dialog path whenever a second level is also unsaved.
  const canRideUnload = useCallback(
    (data) =>
      [1, 2, 3].filter((lvl) => JSON.stringify(data[lvl] || {}) !== lastSavedRef.current[lvl])
        .length <= 1,
    [],
  );
  const { status: saveStatus, markDirty, flush } = useFormAutosave({
    data: formData,
    save: persistLevels,
    enabled: !loading && !!project,
    canRideUnload,
  });

  const formatKey = normalizeFormat(project?.format);

  const handleBeatChange = (level, key, value) => {
    setFormData((prev) => ({ ...prev, [level]: { ...prev[level], [key]: value } }));
    markDirty();
  };

  const toggleGuide = (key) => {
    setOpenGuides((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const changeFocus = (idOrNull) => {
    setFocusId(idOrNull);
    try {
      if (idOrNull) localStorage.setItem(focusStorageKey(projectId), idOrNull);
      else localStorage.removeItem(focusStorageKey(projectId));
    } catch {
      // storage blocked — focus just won't persist
    }
  };

  // Deep link from the /plot/:id/level/N redirect shim.
  const initialSectionId = useMemo(() => {
    const lvl = location.state?.scrollToLevel;
    if (!lvl || !project) return null;
    const beats = beatsFor(formatKey, lvl);
    const idx = Math.max(0, Math.min(location.state?.scrollToBeatIndex ?? 0, beats.length - 1));
    return beats[idx]?.key ?? null;
  }, [location.state, project, formatKey]);

  // Act II parity with the legacy wizard: protagonist enneagram stress
  // warning for feature/novel formats.
  useEffect(() => {
    if (loading || !project) return;
    if (formatKey !== 'feature' && formatKey !== 'novel') return;
    let cancelled = false;
    (async () => {
      try {
        const projectsRes = await authFetch(`${API}/projects`, { credentials: 'include' });
        if (!projectsRes.ok) return;
        const allProjects = await projectsRes.json();
        const protagonist = Array.isArray(allProjects)
          ? allProjects.find(
              (p) => p.project_type === 'character' && p.character_role === 'protagonist' && p.enneagram_type,
            )
          : null;
        if (!protagonist) return;
        const res = await authFetch(`${API}/enneagram/${protagonist.project_id}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStressWarning(data);
      } catch (error) {
        console.error('Error fetching enneagram:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, project, formatKey]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await flush();
      const client = saveClientRef.current?.client;
      const response = await authFetch(`${API}/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          level: 3,
          data: formData[3] || {},
          completed: true,
          base_rev: client?.getRev() ?? null,
        }),
      });
      if (response.status === 409) {
        toast.error(t('saveStatus_conflict'));
        return;
      }
      if (!response.ok) throw new Error('Failed to complete plot');
      const updated = await response.json().catch(() => ({}));
      client?.setRev(updated.content_rev ?? null);
      toast.success('Plot completed!');
    } catch (error) {
      console.error('Error completing plot:', error);
      toast.error('Failed to complete plot');
    } finally {
      setCompleting(false);
    }
  };

  // The "first beat developed" milestone is judged SERVER-side on save (the
  // same devLevel bar, ported), so nothing is reported from here.

  if (loading) {
    return <div className="text-muted-foreground font-mono py-10">Loading…</div>;
  }
  if (!project) {
    return <div className="text-muted-foreground font-mono py-10">Failed to load — reload the page.</div>;
  }

  const renderBeat = (level, beat) => ({ focused, onFocus }) => {
    const guide = forgeGuideText(beat.key, t);
    const open = openGuides.has(beat.key);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor={`canvas-${beat.key}`}
            className="block font-mono text-sm uppercase tracking-wider text-foreground"
          >
            {beat.label}
          </label>
          <button
            type="button"
            onClick={onFocus}
            data-testid={`focus-${beat.key}`}
            title={focused ? 'Show all beats' : 'Focus on this beat'}
            className={`flex-shrink-0 p-1 transition-colors ${
              focused ? 'text-primary' : 'text-muted-foreground/50 hover:text-foreground'
            }`}
          >
            <Crosshair className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* The beat IS the surface — write first. */}
        <AutoExpandTextarea
          id={`canvas-${beat.key}`}
          placeholder={beat.placeholder}
          value={formData[level]?.[beat.key] || ''}
          onChange={(e) => handleBeatChange(level, beat.key, e.target.value)}
          data-testid={`input-${beat.key}`}
          className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 min-h-[120px]"
        />

        {/* Coaching second: quiet, per-beat, closed by default (review #7). */}
        <div className="flex items-center justify-between gap-3">
          {guide ? (
            <button
              type="button"
              onClick={() => toggleGuide(beat.key)}
              data-testid={`forge-guide-toggle-${beat.key}`}
              className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              style={open ? { color: '#E8A020' } : undefined}
            >
              <BookOpen className="w-3 h-3" />
              {open ? t('hideForgeGuide') : t('showForgeGuide')}
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() =>
              navigate(
                `/scenes?beat_key=${beat.key}&plot_project_id=${projectId}&beat_label=${encodeURIComponent(beat.label)}`,
              )
            }
            data-testid={`build-scenes-${beat.key}`}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            <Clapperboard className="w-3 h-3" />
            {t('plot_buildScenes')}
          </button>
        </div>
        {open && guide && (
          <div
            className="p-4 text-sm leading-relaxed font-mono border-l-2"
            style={{ borderColor: '#E8A020', background: 'rgba(232,160,32,0.06)', color: '#E8A020' }}
            data-testid={`forge-guide-${beat.key}`}
          >
            {guide}
          </div>
        )}
      </div>
    );
  };

  const actIIWarning =
    stressWarning?.stress_pattern && (formatKey === 'feature' || formatKey === 'novel') ? (
      <div className="bg-primary/10 border border-primary/30 p-4 mb-6" data-testid="enneagram-warning">
        <p className="font-mono text-xs uppercase text-primary mb-1">
          Enneagram Stress Pattern: {stressWarning.stress_pattern.name}
        </p>
        <p className="text-foreground text-sm leading-relaxed">
          <strong>Warning:</strong> Under extreme stress, your Lead {stressWarning.stress_pattern.stress}.
          How does this specific reaction ruin their current strategy in "The Breaking Point" and "The Hollow"?
        </p>
      </div>
    ) : null;

  const groups = [1, 2, 3].map((lvl) => {
    const cfg = LEVEL_CONFIG[lvl];
    const actTitle = cfg.formatTitles?.[formatKey]?.title || cfg.title;
    const actSubtitle = cfg.formatTitles?.[formatKey]?.subtitle || cfg.subtitle;
    const beats = beatsFor(formatKey, lvl);
    return {
      id: `act-${lvl}`,
      title: actTitle,
      subtitle: actSubtitle,
      banner: lvl === 2 ? actIIWarning : null,
      // Depth, not fill: dots and the milestone judge substance (devLevel),
      // never mere non-emptiness. Every plot beat is required.
      dev: groupDevLevel(beats.map((b) => formData[lvl]?.[b.key] || '')),
      milestoneText: t('milestone_developed', { name: actTitle }),
      sections: beats.map((beat) => ({
        id: beat.key,
        label: beat.label,
        dev: devLevel(formData[lvl]?.[beat.key] || ''),
        render: renderBeat(lvl, beat),
      })),
    };
  });

  return (
    <div data-testid="plot-canvas">
      {/* No counters, no percentages — the dots, ribbon, and milestone marks
          carry depth; the only number a writer needs here is "saved when". */}
      <div className="mb-4 flex items-center justify-end gap-3">
        <SaveStatusLabel status={saveStatus} />
      </div>

      <SectionCanvas
        groups={groups}
        focusId={focusId}
        onFocusChange={changeFocus}
        initialSectionId={initialSectionId}
        onActiveChange={(sectionId, groupId) => {
          const act = parseInt(String(groupId).replace('act-', ''), 10);
          if (act) onActiveActChange?.(act);
        }}
      />

      <div className="mt-4 flex justify-end">
        <Button
          onClick={handleComplete}
          disabled={completing}
          data-testid="canvas-complete-btn"
          className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
          style={{ background: '#E8A020', color: '#000', border: 'none' }}
        >
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {completing ? t('saving') : `${t('plot_complete')} ✓`}
        </Button>
      </div>
    </div>
  );
}
